import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import PageHeader from '@components/layout/page-header.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { Badge } from '@components/atoms/ui-kit/badge';
import { useGetCustomerBoTokens } from '@utils/api/back-office/customer-bo/use-get-customer-bo-tokens.tsx';

type Row = {
  id: number | string;
  name: string;
  key: string;
  type: string;
  expires: string;
};

export default function CustomerBoTokens() {
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId?: string }>();
  const numericId = Number(customerId) || 0;
  const { data: tokensData, isLoading } = useGetCustomerBoTokens(numericId);

  const tokens = tokensData?.result || [];

  const typeMap: Record<number, string> = {
    1: t('reseller'),
    2: t('reseller'),
    3: t('manual')
  };

  const rows = useMemo<Row[]>(() => {
    return tokens.map((token, index) => ({
      id: token.id ?? index,
      key: token.token,
      name: token.name,
      type: typeMap[token.type] ?? t('unknown'),
      expires: token.expiration
    }));
  }, [tokens, t]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: t('name'),
        field: 'name',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      },
      {
        header: t('key'),
        field: 'key',
        minWidth: 260,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: false
      },
      {
        header: t('type'),
        field: 'type',
        maxWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('reseller'), value: t('reseller') },
          { label: t('manual'), value: t('manual') }
        ],
        cell: ({ getValue }) => {
          const label = String(getValue() ?? '');
          const type = label.toLowerCase();
          const color =
            type === t('reseller').toLowerCase()
              ? 'blue'
              : type === t('manual').toLowerCase()
                ? 'green'
                : 'zinc';
          return (
            <Badge color={color as 'blue' | 'green' | 'zinc'}>{label}</Badge>
          );
        }
      },
      {
        header: t('expires'),
        field: 'expires',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      }
    ];
  }, [t]);

  return (
    <div className="min-w-full space-y-4">
      <PageHeader
        title={t('customer-tokens')}
        contentClassName={'py-0 pb-4'}
        caption={t(
          'list-of-all-the-tokens-including-their-name-key-type-and-expiration'
        )}
      />
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection={false}
          enablePagination
          manualPagination
          totalItems={tokensData?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          height="75vh"
          dense
          striped
          grid={false}
          loading={isLoading}
          getRowId={row => String(row.id)}
        />
      )}
    </div>
  );
}
